import React, { useState, useEffect, useRef } from "react";
import {
  FaGraduationCap,
  FaGlobe,
  FaUsers,
  FaAward,
  FaPhone,
  FaEnvelope,
  FaCheckCircle,
  FaArrowRight,
  FaUniversity,
  FaHandshake,
  FaChartLine,
  FaChevronLeft,
  FaChevronRight,
  FaStar,
  FaQuoteLeft,
  FaMobile,
  FaShieldAlt,
  FaBell,
  FaCalendarAlt,
  FaDownload,
} from "react-icons/fa";
import { SpeedInsights } from "@vercel/speed-insights/react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./App.css";
import { onAuthStateChange } from "./firebase";
import {
  getSecureAdminRoute,
  isValidAdminRoute,
  isAdminSessionValid,
  clearAllAdminData,
} from "./utils/adminSecurity";
import CalendlyCalendar from "./components/CalendlyCalendar";
import AppointmentSuccess from "./components/AppointmentSuccess";
import AdminPanel from "./components/AdminPanel";
import AdminLogin from "./components/AdminLogin";
import TwoFactorSetup from "./components/TwoFactorSetup";
import TwoFactorVerification from "./components/TwoFactorVerification";
import SecurityProvider from "./components/SecurityProvider";
import QueryPage from "./components/QueryPage";
import DocumentUpload from "./components/DocumentUpload";
import BlogList from "./components/BlogList";
import BlogPost from "./components/BlogPost";
import BlogSection from "./components/BlogSection";
import { ga4Event } from "./utils/ga4";
import {
  sendAppointmentConfirmationEmail,
  sendAdminNotificationEmail,
  formatUserDataForEmail,
  formatAppointmentDataForEmail,
  saveUserInfo,
  saveIncompleteAppointment,
  cleanupIncompleteAppointment,
} from "./utils/emailUtils";
import {
  useAnalytics,
  useStepTracking,
  useFormFieldTracking,
} from "./hooks/useAnalytics";

function App() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    age: "",
    education: "",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentUniversitySlide, setCurrentUniversitySlide] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  // Initialize step/slug from the URL so blog deep-links render without a flash
  const getBlogRouteFromUrl = () => {
    const path = window.location.pathname;
    const match = path.match(/^\/blog\/(.+?)\/?$/);
    if (match) {
      return { step: "blog-post", slug: decodeURIComponent(match[1]) };
    }
    if (path === "/blog" || path === "/blog/") {
      return { step: "blog", slug: null };
    }
    return null;
  };
  const initialBlogRoute = getBlogRouteFromUrl();
  const [currentStep, setCurrentStep] = useState(
    initialBlogRoute ? initialBlogRoute.step : "form"
  ); // 'form', 'calendar', 'success', 'admin', 'admin-login', '2fa-setup', '2fa-verify', 'query', 'document-upload', 'blog', 'blog-post'
  const [blogSlug, setBlogSlug] = useState(
    initialBlogRoute ? initialBlogRoute.slug : null
  );
  const [userInfo, setUserInfo] = useState(null);
  const [appointmentData, setAppointmentData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [adminUser, setAdminUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true); // Firebase Auth loading state
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingUserEmail, setPendingUserEmail] = useState(null);
  const [pendingUserData, setPendingUserData] = useState(null);
  const [processStepsVisible, setProcessStepsVisible] = useState([]);
  const processStepsRef = useRef([]);

  // Analytics hooks
  const analytics = useAnalytics();
  const trackStep = useStepTracking();
  const { createFieldProps } = useFormFieldTracking();

  // Partner üniversiteler data
  const universities = [
    {
      name: "TU Berlin",
      logo: "data:image/svg+xml,%3Csvg width='120' height='120' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='grad1' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%234A90E2;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%23357ABD;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='120' height='120' fill='url(%23grad1)' rx='15'/%3E%3Ctext x='60' y='45' font-family='Arial,sans-serif' font-size='16' font-weight='bold' text-anchor='middle' fill='white'%3ETU%3C/text%3E%3Ctext x='60' y='65' font-family='Arial,sans-serif' font-size='14' text-anchor='middle' fill='white'%3EBerlin%3C/text%3E%3Ccircle cx='60' cy='85' r='8' fill='white' opacity='0.3'/%3E%3C/svg%3E",
    },
    {
      name: "LMU München",
      logo: "data:image/svg+xml,%3Csvg width='120' height='120' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='grad2' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%2350C878;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%233A9B5C;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='120' height='120' fill='url(%23grad2)' rx='15'/%3E%3Ctext x='60' y='50' font-family='Arial,sans-serif' font-size='18' font-weight='bold' text-anchor='middle' fill='white'%3ELMU%3C/text%3E%3Ctext x='60' y='70' font-family='Arial,sans-serif' font-size='12' text-anchor='middle' fill='white'%3EMünchen%3C/text%3E%3Cpolygon points='60,80 70,90 60,100 50,90' fill='white' opacity='0.3'/%3E%3C/svg%3E",
    },
    {
      name: "Heidelberg Uni",
      logo: "data:image/svg+xml,%3Csvg width='120' height='120' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='grad3' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23FF6B6B;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%23E55555;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='120' height='120' fill='url(%23grad3)' rx='15'/%3E%3Ctext x='60' y='45' font-family='Arial,sans-serif' font-size='14' font-weight='bold' text-anchor='middle' fill='white'%3EHeidelberg%3C/text%3E%3Ctext x='60' y='65' font-family='Arial,sans-serif' font-size='16' text-anchor='middle' fill='white'%3EUni%3C/text%3E%3Crect x='45' y='75' width='30' height='20' fill='white' opacity='0.3' rx='3'/%3E%3C/svg%3E",
    },
    {
      name: "RWTH Aachen",
      logo: "data:image/svg+xml,%3Csvg width='120' height='120' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='grad4' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%234ECDC4;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%2339A39A;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='120' height='120' fill='url(%23grad4)' rx='15'/%3E%3Ctext x='60' y='45' font-family='Arial,sans-serif' font-size='16' font-weight='bold' text-anchor='middle' fill='white'%3ERWTH%3C/text%3E%3Ctext x='60' y='65' font-family='Arial,sans-serif' font-size='14' text-anchor='middle' fill='white'%3EAachen%3C/text%3E%3Cpath d='M45 80 L60 75 L75 80 L75 95 L45 95 Z' fill='white' opacity='0.3'/%3E%3C/svg%3E",
    },
    {
      name: "Uni Hamburg",
      logo: "data:image/svg+xml,%3Csvg width='120' height='120' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='grad5' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%2345B7D1;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%23339BB8;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='120' height='120' fill='url(%23grad5)' rx='15'/%3E%3Ctext x='60' y='45' font-family='Arial,sans-serif' font-size='16' font-weight='bold' text-anchor='middle' fill='white'%3EUni%3C/text%3E%3Ctext x='60' y='65' font-family='Arial,sans-serif' font-size='14' text-anchor='middle' fill='white'%3EHamburg%3C/text%3E%3Cpath d='M35 80 Q60 70 85 80 Q60 90 35 80' fill='white' opacity='0.3'/%3E%3C/svg%3E",
    },
    {
      name: "TU Dresden",
      logo: "data:image/svg+xml,%3Csvg width='120' height='120' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='grad6' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23F7DC6F;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%23F4D03F;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='120' height='120' fill='url(%23grad6)' rx='15'/%3E%3Ctext x='60' y='45' font-family='Arial,sans-serif' font-size='16' font-weight='bold' text-anchor='middle' fill='%23333'%3ETU%3C/text%3E%3Ctext x='60' y='65' font-family='Arial,sans-serif' font-size='14' text-anchor='middle' fill='%23333'%3EDresden%3C/text%3E%3Cstar cx='60' cy='85' r1='12' r2='6' fill='%23333' opacity='0.3'/%3E%3C/svg%3E",
    },
    {
      name: "Uni Frankfurt",
      logo: "data:image/svg+xml,%3Csvg width='120' height='120' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='grad7' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23BB8FCE;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%23A569BD;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='120' height='120' fill='url(%23grad7)' rx='15'/%3E%3Ctext x='60' y='45' font-family='Arial,sans-serif' font-size='16' font-weight='bold' text-anchor='middle' fill='white'%3EUni%3C/text%3E%3Ctext x='60' y='65' font-family='Arial,sans-serif' font-size='12' text-anchor='middle' fill='white'%3EFrankfurt%3C/text%3E%3Ccircle cx='50' cy='85' r='6' fill='white' opacity='0.3'/%3E%3Ccircle cx='70' cy='85' r='6' fill='white' opacity='0.3'/%3E%3C/svg%3E",
    },
    {
      name: "KIT Karlsruhe",
      logo: "data:image/svg+xml,%3Csvg width='120' height='120' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='grad8' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%2385C1E9;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%236FADDC;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='120' height='120' fill='url(%23grad8)' rx='15'/%3E%3Ctext x='60' y='45' font-family='Arial,sans-serif' font-size='18' font-weight='bold' text-anchor='middle' fill='white'%3EKIT%3C/text%3E%3Ctext x='60' y='65' font-family='Arial,sans-serif' font-size='12' text-anchor='middle' fill='white'%3EKarlsruhe%3C/text%3E%3Cpath d='M40 80 L60 75 L80 80 L70 90 L50 90 Z' fill='white' opacity='0.3'/%3E%3C/svg%3E",
    },
  ];

  // Testimonials data
  const testimonials = [
    {
      name: "Karmaşa Bitti",
      university: "TU Dortmund - Bauingenieurwesen",
      image:
        "data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='tgrad1' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%234A90E2;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%23357ABD;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Ccircle cx='40' cy='40' r='40' fill='url(%23tgrad1)'/%3E%3Ctext x='40' y='50' font-family='Arial,sans-serif' font-size='28' font-weight='bold' text-anchor='middle' fill='white'%3EK%3C/text%3E%3C/svg%3E",
      rating: 5,
      text: "Almanya'da eğitim sürecinde ne yapacağımı hiç bilmiyordum. Üniversite seçimi, vize işlemleri derken işler iyice karışacak diye korkuyordum. Ama Almanya Kampüs sayesinde her şey o kadar kolay oldu ki! Karmaşa bitti ve hayallerime çok daha hızlı yaklaştım.",
    },
    {
      name: "Başarı Hikayemi Yazdım",
      university: "FHS Dortmund - Biomedizintechnik",
      image:
        "data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='tgrad2' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%2350C878;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%233A9B5C;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Ccircle cx='40' cy='40' r='40' fill='url(%23tgrad2)'/%3E%3Ctext x='40' y='50' font-family='Arial,sans-serif' font-size='28' font-weight='bold' text-anchor='middle' fill='white'%3EB%3C/text%3E%3C/svg%3E",
      rating: 5,
      text: "Almanya Kampüs sadece Almanya'da eğitim başvurularını yapmanıza yardımcı olmuyor, aynı zamanda bu süreçte kendi başarı hikayenizi yazmanızı sağlıyor! Her şey o kadar hızlı ve kolay ki, öğrencilik hayatınızı değiştirmek haydi olmaktan çıkıyor.",
    },
    {
      name: "Hayalimi Gerçekleştirdim",
      university: "DUE - Wirtschaftsinformatik",
      image:
        "data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='tgrad3' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23FF6B6B;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%23E55555;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Ccircle cx='40' cy='40' r='40' fill='url(%23tgrad3)'/%3E%3Ctext x='40' y='50' font-family='Arial,sans-serif' font-size='28' font-weight='bold' text-anchor='middle' fill='white'%3EH%3C/text%3E%3C/svg%3E",
      rating: 5,
      text: "Almanya Kampüs ile süreç başladım ve birkaç ay içinde Almanya'da ücretsiz ve dünya çapında kaliteli bir eğitime ulaşmanın yolunu buldum. Şu an hem hayalimi gerçeğe dönüştürüyorum hem de geleceğime daha aydınlık bakabiliyorum.",
    },
  ];

  // Authentication listener
  useEffect(() => {
    const unsubscribe = onAuthStateChange((user) => {
      setAdminUser(user);
      setAuthLoading(false); // Auth state loaded
    });

    return () => unsubscribe();
  }, []);

  // Check for admin URL access with secure routing
  useEffect(() => {
    // Don't run admin route check until Firebase Auth is loaded
    if (authLoading) return;

    const checkAdminAccess = () => {
      const path = window.location.pathname.replace("/", "");
      const hash = window.location.hash.replace("#", "");
      const secureRoute = getSecureAdminRoute();

      // Check for query route
      if (path === "sorgula" || hash === "sorgula") {
        if (currentStep !== "query") {
          setCurrentStep("query");
        }
        return;
      }

      // Check for document upload route
      if (path === "dosya-yukle" || hash === "dosya-yukle") {
        if (currentStep !== "document-upload") {
          setCurrentStep("document-upload");
        }
        return;
      }

      // Check for old insecure routes and redirect
      if (path === "admin" || hash === "admin") {
        alert(
          "Bu erişim yolu artık geçerli değil. Lütfen /admin-panel kullanın."
        );
        window.history.replaceState({}, document.title, "/");
        return;
      }

      // Check for secure admin route
      if (path === secureRoute || hash === secureRoute) {
        if (isValidAdminRoute(path || hash)) {
          // Only change step if we're not already in an admin-related step
          if (
            !["admin", "admin-login", "2fa-setup", "2fa-verify"].includes(
              currentStep
            )
          ) {
            if (adminUser && isAdminSessionValid()) {
              setCurrentStep("admin");
            } else {
              // Clear any invalid session data
              clearAllAdminData();
              setCurrentStep("admin-login");
            }
          }
        } else {
          alert("Geçersiz admin erişim rotası.");
          window.history.replaceState({}, document.title, "/");
        }
        // Clean the URL for security
        window.history.replaceState({}, document.title, "/");
      }
    };

    checkAdminAccess();
    window.addEventListener("popstate", checkAdminAccess);

    return () => window.removeEventListener("popstate", checkAdminAccess);
  }, [adminUser, currentStep, authLoading]);

  // Auto slider effects
  useEffect(() => {
    const universityInterval = setInterval(() => {
      setCurrentUniversitySlide((prev) =>
        prev === universities.length - 4 ? 0 : prev + 1
      );
    }, 3000);

    const testimonialInterval = setInterval(() => {
      setCurrentTestimonial((prev) =>
        prev === testimonials.length - 1 ? 0 : prev + 1
      );
    }, 5000);

    return () => {
      clearInterval(universityInterval);
      clearInterval(testimonialInterval);
    };
  }, [universities.length, testimonials.length]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoadingMessage("Bilgileriniz kaydediliyor...");

    // Track form submission
    analytics.trackFormSubmission(formData, true);

    try {
      // Save user info to Firebase
      const userId = await saveUserInfo(formData);

      // Save incomplete appointment for admin tracking
      setLoadingMessage("Form bilgileriniz kaydediliyor...");
      try {
        await saveIncompleteAppointment(formData);
        console.log("Incomplete appointment saved for user:", formData.email);
      } catch (incompleteError) {
        console.warn("Failed to save incomplete appointment:", incompleteError);
        // Don't fail the main flow if this fails
      }

      // Store user info with userId for next step
      const userInfoWithId = {
        ...formData,
        userId: userId,
      };

      setUserInfo(userInfoWithId);

      // Emails will be sent only after appointment date is selected
      setLoadingMessage("Randevu takvimi hazırlanıyor...");

      // Small delay to show the message
      await new Promise((resolve) => setTimeout(resolve, 500));

      setCurrentStep("calendar");
      setIsSubmitted(true);
    } catch (error) {
      console.error("Error saving user data:", error);
      alert("Bilgiler kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.");
    }

    setIsLoading(false);
    setLoadingMessage("");
  };

  const scrollToForm = () => {
    // Check if we're on mobile (screen width <= 1024px)
    const isMobile = window.innerWidth <= 1024;

    if (isMobile) {
      // On mobile, scroll directly to the form element (not the contact info)
      const formElement = document.querySelector(".contact-form");
      if (formElement) {
        formElement.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        // Fallback to contact-form section
        document
          .getElementById("contact-form")
          .scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // On desktop, scroll to the contact-form section normally
      document
        .getElementById("contact-form")
        .scrollIntoView({ behavior: "smooth" });
    }
  };

  // Scroll-based animation for process steps
  useEffect(() => {
    const observerOptions = {
      threshold: 0.3, // Element becomes visible when 30% is in view
      rootMargin: "-50px 0px -50px 0px", // Trigger animation earlier
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const stepIndex = parseInt(entry.target.dataset.stepIndex);
        if (entry.isIntersecting) {
          // Staggered animation - add delay for each step
          setTimeout(() => {
            setProcessStepsVisible((prev) => {
              if (!prev.includes(stepIndex)) {
                return [...prev, stepIndex].sort((a, b) => a - b);
              }
              return prev;
            });
          }, stepIndex * 150); // 150ms delay between each step
        }
      });
    }, observerOptions);

    // Observe all process step elements
    processStepsRef.current.forEach((step, index) => {
      if (step) {
        step.dataset.stepIndex = index;
        observer.observe(step);
      }
    });

    return () => {
      processStepsRef.current.forEach((step) => {
        if (step) observer.unobserve(step);
      });
    };
  }, []);

  // University slider functions
  const nextUniversitySlide = () => {
    setCurrentUniversitySlide((prev) =>
      prev === universities.length - 4 ? 0 : prev + 1
    );
  };

  const prevUniversitySlide = () => {
    setCurrentUniversitySlide((prev) =>
      prev === 0 ? universities.length - 4 : prev - 1
    );
  };

  // Testimonial functions
  const nextTestimonial = () => {
    setCurrentTestimonial((prev) =>
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) =>
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  // Calendar flow handlers
  const handleBackToForm = () => {
    setCurrentStep("form");
    setUserInfo(null);
    setIsSubmitted(false);
  };

  const handleAppointmentComplete = async (appointment) => {
    setAppointmentData(appointment);

    // Track successful appointment completion
    analytics.completeSession("appointment_booked");
    // GA4 dönüşüm eventi (çapraz kontrol)
    ga4Event("generate_lead", { value: 1, currency: "EUR" });

    // Send final appointment confirmation email with appointment details
    if (userInfo && appointment) {
      try {
        const emailData = formatUserDataForEmail(userInfo);
        const appointmentEmailData = formatAppointmentDataForEmail(appointment);

        // Send confirmation email to user
        await sendAppointmentConfirmationEmail(emailData, appointmentEmailData);
        console.log("Final appointment confirmation email sent successfully");
      } catch (emailError) {
        console.error(
          "Error sending final appointment confirmation:",
          emailError
        );
        // Don't block the flow if email fails
      }

      // Send updated admin notification with appointment details
      try {
        const emailData = formatUserDataForEmail(userInfo);
        const appointmentEmailData = formatAppointmentDataForEmail(appointment);

        // Send notification to admin
        await sendAdminNotificationEmail(emailData, appointmentEmailData);
        console.log("Updated admin notification sent successfully");
      } catch (emailError) {
        console.error("Error sending updated admin notification:", emailError);
        // Don't block the flow if email fails
      }

      // Clean up incomplete appointment since full appointment is now completed
      try {
        await cleanupIncompleteAppointment(userInfo.email);
        console.log("Incomplete appointment cleaned up for:", userInfo.email);
      } catch (cleanupError) {
        console.warn("Failed to cleanup incomplete appointment:", cleanupError);
        // Don't block the flow if cleanup fails
      }
    }

    setCurrentStep("success");
  };

  const handleBackToHome = () => {
    setCurrentStep("form");
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      age: "",
      education: "",
    });
    setUserInfo(null);
    setAppointmentData(null);
    setIsSubmitted(false);
    // Update URL to home page
    window.history.replaceState({}, document.title, "/");
  };

  // ---- Blog routing & navigation ----
  // Keep blog URLs in the address bar (good for SEO & sharing) and sync
  // browser back/forward navigation with the SPA state.
  useEffect(() => {
    const handleBlogPopState = () => {
      const route = getBlogRouteFromUrl();
      if (route) {
        setBlogSlug(route.slug);
        setCurrentStep(route.step);
      } else if (
        window.location.pathname === "/" &&
        ["blog", "blog-post"].includes(currentStep)
      ) {
        // Navigated back from a blog page to the homepage
        setBlogSlug(null);
        setCurrentStep("form");
      }
    };
    window.addEventListener("popstate", handleBlogPopState);
    return () => window.removeEventListener("popstate", handleBlogPopState);
  }, [currentStep]);

  const navigateToBlogList = () => {
    window.history.pushState({}, "", "/blog");
    setBlogSlug(null);
    setCurrentStep("blog");
    window.scrollTo(0, 0);
  };

  const navigateToBlogPost = (slug) => {
    window.history.pushState({}, "", `/blog/${slug}`);
    setBlogSlug(slug);
    setCurrentStep("blog-post");
    window.scrollTo(0, 0);
  };

  const navigateHomeFromBlog = () => {
    window.history.pushState({}, "", "/");
    setBlogSlug(null);
    setCurrentStep("form");
    window.scrollTo(0, 0);
  };

  // Blog'dan "Görüşme Randevusu Al" -> anasayfadaki randevu formuna ışınla
  const navigateToBookingFromBlog = () => {
    window.history.pushState({}, "", "/");
    setBlogSlug(null);
    setCurrentStep("form");
    setTimeout(() => scrollToForm(), 120);
  };

  // Simple admin route info
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log(
        "🔐 Admin Panel: Navigate to /#admin-panel to access admin features"
      );
    }
  }, []);

  const handleAdminLoginSuccess = (user) => {
    setAdminUser(user);
    setCurrentStep("admin");
  };

  const handleRequire2FA = (user, email, userData) => {
    setPendingUser(user);
    setPendingUserEmail(email);
    setPendingUserData(userData); // Store user data including role

    // Check if user has 2FA enabled from Firebase data
    if (userData.twoFactorEnabled) {
      // 2FA is enabled, show verification
      setCurrentStep("2fa-verify");
    } else {
      // 2FA is NOT enabled, FORCE setup (mandatory)
      setCurrentStep("2fa-setup");
    }
  };

  const handle2FASetupComplete = () => {
    if (pendingUser) {
      const {
        setAdminSession,
        clearLoginAttempts,
        logAdminActivity,
        set2FARememberToken,
      } = require("./utils/adminSecurity");

      // Complete login after 2FA setup (include role data)
      setAdminSession(pendingUser, pendingUserData);
      clearLoginAttempts(pendingUserEmail);

      // Set 2FA remember token for 30 minutes
      set2FARememberToken(pendingUserEmail, pendingUser.uid);

      logAdminActivity("login_success_with_2fa_setup", {
        email: pendingUserEmail,
        timestamp: new Date().toISOString(),
        uid: pendingUser.uid,
        role: pendingUserData?.role || "admin",
      });

      setAdminUser(pendingUser);
      setPendingUser(null);
      setPendingUserEmail(null);
      setPendingUserData(null);
      setCurrentStep("admin");
    }
  };

  const handle2FAVerificationSuccess = () => {
    if (pendingUser) {
      const {
        setAdminSession,
        clearLoginAttempts,
        logAdminActivity,
        set2FARememberToken,
      } = require("./utils/adminSecurity");

      // Complete login after 2FA verification (include role data)
      setAdminSession(pendingUser, pendingUserData);
      clearLoginAttempts(pendingUserEmail);

      // Set 2FA remember token for 30 minutes
      set2FARememberToken(pendingUserEmail, pendingUser.uid);

      logAdminActivity("login_success_with_2fa", {
        email: pendingUserEmail,
        timestamp: new Date().toISOString(),
        uid: pendingUser.uid,
        role: pendingUserData?.role || "admin",
      });

      setAdminUser(pendingUser);
      setPendingUser(null);
      setPendingUserEmail(null);
      setPendingUserData(null);
      setCurrentStep("admin");
    }
  };

  const handleCancel2FA = () => {
    setPendingUser(null);
    setPendingUserEmail(null);
    setPendingUserData(null);
    setCurrentStep("admin-login");
  };

  const handleAdminLogout = () => {
    clearAllAdminData();
    setAdminUser(null);
    setPendingUser(null);
    setPendingUserEmail(null);
    setPendingUserData(null);
    setCurrentStep("form");

    // Clear any admin-related URL traces
    window.history.replaceState({}, document.title, "/");
  };

  // If admin step but invalid session, redirect to login (but not during initial load)
  useEffect(() => {
    // Don't check session until Firebase Auth is loaded
    if (authLoading) return;

    if (currentStep === "admin" && (!adminUser || !isAdminSessionValid())) {
      clearAllAdminData();
      setCurrentStep("admin-login");
    }
  }, [currentStep, adminUser, authLoading]);

  // Analytics: Track step changes
  useEffect(() => {
    trackStep(currentStep);
  }, [currentStep, trackStep]);

  // Show loading screen while Firebase Auth is loading and we're on admin route
  if (authLoading) {
    const path = window.location.pathname.replace("/", "");
    const hash = window.location.hash.replace("#", "");
    const secureRoute = getSecureAdminRoute();

    if (path === secureRoute || hash === secureRoute) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            fontFamily: "Arial, sans-serif",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "50px",
                height: "50px",
                border: "3px solid rgba(255,255,255,0.3)",
                borderTop: "3px solid white",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            ></div>
            <h3>Admin Panel Yükleniyor...</h3>
            <p>Kimlik doğrulama kontrol ediliyor</p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      );
    }
  }

  // Render blog list
  if (currentStep === "blog") {
    return (
      <BlogList
        onOpenPost={navigateToBlogPost}
        onBook={navigateToBookingFromBlog}
        onHome={navigateHomeFromBlog}
      />
    );
  }

  // Render single blog post
  if (currentStep === "blog-post") {
    return (
      <BlogPost
        slug={blogSlug}
        onOpenList={navigateToBlogList}
        onOpenPost={navigateToBlogPost}
        onBook={navigateToBookingFromBlog}
        onHome={navigateHomeFromBlog}
      />
    );
  }

  // Render calendar view
  if (currentStep === "calendar" && userInfo) {
    return (
      <CalendlyCalendar
        userInfo={userInfo}
        onBack={handleBackToForm}
        onComplete={handleAppointmentComplete}
      />
    );
  }

  // Render success view
  if (currentStep === "success" && appointmentData) {
    return (
      <AppointmentSuccess
        appointmentData={appointmentData}
        onBackToHome={handleBackToHome}
      />
    );
  }

  // Render admin login
  if (currentStep === "admin-login") {
    return (
      <AdminLogin
        onLoginSuccess={handleAdminLoginSuccess}
        onRequire2FA={handleRequire2FA}
        onBackToHome={handleBackToHome}
      />
    );
  }

  // Render 2FA setup
  if (currentStep === "2fa-setup" && pendingUserEmail && pendingUser) {
    return (
      <TwoFactorSetup
        userEmail={pendingUserEmail}
        userUid={pendingUser.uid}
        onSetupComplete={handle2FASetupComplete}
        onCancel={handleCancel2FA}
      />
    );
  }

  // Render 2FA verification
  if (currentStep === "2fa-verify" && pendingUserEmail && pendingUser) {
    return (
      <TwoFactorVerification
        userEmail={pendingUserEmail}
        userUid={pendingUser.uid}
        onVerificationSuccess={handle2FAVerificationSuccess}
        onCancel={handleCancel2FA}
      />
    );
  }

  // Render query page
  if (currentStep === "query") {
    return <QueryPage onBackToHome={handleBackToHome} />;
  }

  // Render document upload page
  if (currentStep === "document-upload") {
    return <DocumentUpload onBackToHome={handleBackToHome} />;
  }

  // Render admin panel (only if authenticated and session is valid)
  if (currentStep === "admin" && adminUser && isAdminSessionValid()) {
    return (
      <AdminPanel
        onBackToHome={handleBackToHome}
        onLogout={handleAdminLogout}
      />
    );
  }

  return (
    <SecurityProvider>
      <div className="App">
        {/* Hero Section */}
        <section className="hero">
          <div className="hero-background"></div>
          <div className="container">
            <div className="hero-content">
              <div className="hero-logo">
                <img
                  src="/logo.png"
                  alt="Almanya Kampüs - Sıfır Almanca ile Almanya'da Ücretsiz Üniversite Eğitimi Danışmanlığı"
                  width="180"
                  height="180"
                />
              </div>
              <div className="hero-text">
                <h1>
                  <span className="highlight">Sıfır Almanca ile</span>{" "}
                  Almanya'da{" "}
                  <span className="highlight">
                    Ücretsiz Eğitim Hayalinizi Gerçekleştirin
                  </span>
                </h1>
                <p className="hero-description">
                  Almanca bilmeden Almanya'da dünya standartlarında ücretsiz
                  eğitim almanız için size rehberlik ediyoruz. 0 Almanca
                  seviyesiyle başvuru yapabilir, ücretsiz görüşme hizmetimizle
                  başvuru sürecinizden vize işlemlerinize kadar her adımda
                  yanınızdayız.
                </p>
                <div className="hero-stats">
                  <div className="stat">
                    <span className="stat-number">500+</span>
                    <span className="stat-label">Başarılı Öğrenci</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number">50+</span>
                    <span className="stat-label">Partner Üniversite</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number">%98</span>
                    <span className="stat-label">Başarı Oranı</span>
                  </div>
                </div>
                <button
                  className="cta-button primary"
                  onClick={() => {
                    analytics.trackButtonClick("hero-cta", { section: "hero" });
                    scrollToForm();
                  }}
                >
                  <FaGraduationCap /> Ücretsiz Görüşme Randevusu Al
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="stats-section">
          <div className="container">
            <div className="stats-content">
              <div className="stats-text">
                <h2>
                  <span className="highlight-blue">
                    0 Almanca ile Ücretsiz Eğitim
                  </span>{" "}
                  İmkanıyla
                  <br />
                  Almanya Yolculuğunuza{" "}
                  <span className="highlight-blue">Profesyonel</span> Rehberlik
                </h2>
                <button className="cta-button blue" onClick={scrollToForm}>
                  ÜCRETSİZ GÖRÜŞME AL
                </button>
              </div>
              <div className="stats-features">
                <div className="feature-item">
                  <FaCheckCircle className="feature-icon" />
                  <span>
                    Almanca bilmeden Avrupa'nın en kaliteli ücretsiz eğitim
                    sisteminde yerinizi alın
                  </span>
                </div>
                <div className="feature-item">
                  <FaCheckCircle className="feature-icon" />
                  <span>
                    Düşük yaşam maliyeti ile yüksek kaliteli yaşam standardı
                  </span>
                </div>
                <div className="feature-item">
                  <FaCheckCircle className="feature-icon" />
                  <span>
                    Mezuniyet sonrası güçlü kariyer fırsatları ve iş imkanları
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="benefits">
          <div className="container">
            <h2 className="section-title">
              Neden 0 Almanca ile Almanya'da Ücretsiz Eğitim?
            </h2>
            <div className="benefits-grid">
              <div className="benefit-card">
                <FaUniversity className="benefit-icon" />
                <h3>Dünya Çapında Tanınan Diplomalar</h3>
                <p>
                  Almanya üniversitelerinden alacağınız diploma dünya genelinde
                  kabul görmekte ve kariyer fırsatlarınızı artırmaktadır.
                </p>
              </div>
              <div className="benefit-card">
                <FaGlobe className="benefit-icon" />
                <h3>Uluslararası Perspektif</h3>
                <p>
                  Çok kültürlü ortamda eğitim alarak global bakış açısı kazanın
                  ve uluslararası network oluşturun.
                </p>
              </div>
              <div className="benefit-card">
                <FaChartLine className="benefit-icon" />
                <h3>Güçlü Ekonomi & İş Fırsatları</h3>
                <p>
                  Avrupa'nın en güçlü ekonomisine sahip Almanya'da mezuniyet
                  sonrası mükemmel iş imkanları sizi bekliyor.
                </p>
              </div>
              <div className="benefit-card">
                <FaAward className="benefit-icon" />
                <h3>Ücretsiz Eğitim ve Uygun Maliyetli Yaşam</h3>
                <p>
                  Devlet üniversitelerinde tamamen ücretsiz eğitim fırsatları ve
                  düşük yaşam maliyeti ile kaliteli eğitim alma imkanı. 0
                  Almanca ile başvuru yapabilirsiniz.
                </p>
              </div>
              <div className="benefit-card">
                <FaUsers className="benefit-icon" />
                <h3>Araştırma ve İnovasyon</h3>
                <p>
                  Dünyanın en iyi araştırma merkezlerinde çalışma ve teknoloji
                  geliştirme fırsatları.
                </p>
              </div>
              <div className="benefit-card">
                <FaHandshake className="benefit-icon" />
                <h3>Yaşam Kalitesi</h3>
                <p>
                  Yüksek yaşam standardı, güvenli ortam ve zengin kültürel yaşam
                  imkanları.
                </p>
              </div>
            </div>
            <div className="benefits-cta">
              <button className="cta-button secondary" onClick={scrollToForm}>
                <FaArrowRight /> Hemen Başvuru Sürecini Başlat
              </button>
            </div>
          </div>
        </section>

        {/* How We Work Section */}
        <section className="how-we-work">
          <div className="container">
            <h2 className="section-title">Nasıl Çalışıyoruz?</h2>
            <p className="section-subtitle">
              0 Almanca ile Almanya'da ücretsiz eğitim hayalinizi
              gerçekleştirmek için 5 adımlık güvenilir sürecimiz
            </p>
            <div className="process-steps">
              <div
                className={`process-step ${
                  processStepsVisible.includes(0) ? "step-animate" : ""
                }`}
                ref={(el) => (processStepsRef.current[0] = el)}
              >
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Ön Görüşme</h3>
                  <p>Hedeflerinizi ve mevcut durumunuzu değerlendiriyoruz</p>
                  <div className="step-icon">
                    <FaUsers />
                  </div>
                </div>
              </div>

              <div
                className={`process-step ${
                  processStepsVisible.includes(1) ? "step-animate" : ""
                }`}
                ref={(el) => (processStepsRef.current[1] = el)}
              >
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Programa Kabul</h3>
                  <p>
                    Size özel eğitim planınızı oluşturup programa dahil ediyoruz
                  </p>
                  <div className="step-icon">
                    <FaChartLine />
                  </div>
                </div>
              </div>

              <div
                className={`process-step ${
                  processStepsVisible.includes(2) ? "step-animate" : ""
                }`}
                ref={(el) => (processStepsRef.current[2] = el)}
              >
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Üniversite Seçimi</h3>
                  <p>Size en uygun üniversite ve bölümleri belirliyoruz</p>
                  <div className="step-icon">
                    <FaUniversity />
                  </div>
                </div>
              </div>

              <div
                className={`process-step ${
                  processStepsVisible.includes(3) ? "step-animate" : ""
                }`}
                ref={(el) => (processStepsRef.current[3] = el)}
              >
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Başvuru Süreci</h3>
                  <p>Tüm belgelerinizi hazırlayıp başvurularınızı yapıyoruz</p>
                  <div className="step-icon">
                    <FaHandshake />
                  </div>
                </div>
              </div>

              <div
                className={`process-step ${
                  processStepsVisible.includes(4) ? "step-animate" : ""
                }`}
                ref={(el) => (processStepsRef.current[4] = el)}
              >
                <div className="step-number">5</div>
                <div className="step-content">
                  <h3>Vize ve Gidiş</h3>
                  <p>
                    Vize sürecinizden Almanya'ya gidişinize kadar yanınızdayız
                  </p>
                  <div className="step-icon">
                    <FaGlobe />
                  </div>
                </div>
              </div>
            </div>

            <div className="process-cta">
              <button className="cta-button primary" onClick={scrollToForm}>
                <FaArrowRight /> Sürecimize Katılın ve Başarıya Ulaşın
              </button>
            </div>
          </div>
        </section>

        {/* Universities Section */}
        {/*
        <section className="universities">
          <div className="container">
            <h2 className="section-title">Partner Üniversitelerimiz</h2>
            <p className="universities-subtitle">
              Almanya'nın en prestijli üniversiteleri ile işbirliği halinde
              çalışıyoruz
            </p>
            <div className="universities-slider">
              <button className="slider-btn prev" onClick={prevUniversitySlide}>
                <FaChevronLeft />
              </button>
              <div className="universities-container">
                <div
                  className="universities-track"
                  style={{
                    transform: `translateX(-${currentUniversitySlide * 25}%)`,
                  }}
                >
                  {universities.map((uni, index) => (
                    <div key={index} className="university-item">
                      <div className="university-logo">
                        <img src={uni.logo} alt={uni.name} />
                      </div>
                      <h4>{uni.name}</h4>
                    </div>
                  ))}
                </div>
              </div>
              <button className="slider-btn next" onClick={nextUniversitySlide}>
                <FaChevronRight />
              </button>
            </div>
          </div>
        </section>
        */}

        {/* Testimonials Section */}
        <section className="testimonials">
          <div className="container">
            <h2 className="section-title">
              Derece Öğrencileri Almanya Kampüs'ü Tercih Ediyor
            </h2>
            <div className="testimonials-slider">
              <button className="slider-btn prev" onClick={prevTestimonial}>
                <FaChevronLeft />
              </button>
              <div className="testimonial-container">
                <div className="testimonial-card">
                  <FaQuoteLeft className="quote-icon" />
                  <div className="testimonial-content">
                    <div className="testimonial-header">
                      <div className="testimonial-image">
                        <img
                          src={testimonials[currentTestimonial].image}
                          alt={testimonials[currentTestimonial].name}
                        />
                      </div>
                      <div className="testimonial-info">
                        <h4>"{testimonials[currentTestimonial].name}"</h4>
                        <p>{testimonials[currentTestimonial].university}</p>
                        <div className="rating">
                          {[
                            ...Array(testimonials[currentTestimonial].rating),
                          ].map((_, i) => (
                            <FaStar key={i} className="star" />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="testimonial-text">
                      {testimonials[currentTestimonial].text}
                    </p>
                  </div>
                </div>
              </div>
              <button className="slider-btn next" onClick={nextTestimonial}>
                <FaChevronRight />
              </button>
            </div>
          </div>
        </section>

        {/* Mobile App Section */}
        <section className="mobile-app-section">
          <div className="container">
            <div className="mobile-app-content">
              <div className="mobile-app-text">
                <div className="mobile-badge">
                  <FaMobile className="mobile-badge-icon" />
                  <span>Mobil Uygulamamız</span>
                </div>
                <h2 className="mobile-app-title">
                  <span className="highlight">Almanya Yolculuğunuz</span>
                  <br />
                  Artık Cebinizde
                </h2>
                <p className="mobile-app-description">
                  Almanya Kampüs mobil uygulaması ile başvuru sürecinizi takip
                  edin, randevularınızı yönetin ve uzman danışmanlarımızla anlık
                  iletişim kurun. Almanya'da eğitim hayalinizi gerçekleştirme
                  yolculuğunuzda her zaman yanınızda.
                </p>

                <div className="mobile-features">
                  <div className="mobile-feature">
                    <FaCalendarAlt className="mobile-feature-icon" />
                    <div>
                      <h4>Randevu Yönetimi</h4>
                      <p>Randevularınızı kolayca planlayın ve takip edin</p>
                    </div>
                  </div>
                  <div className="mobile-feature">
                    <FaBell className="mobile-feature-icon" />
                    <div>
                      <h4>Anlık Bildirimler</h4>
                      <p>Önemli güncellemelerden hemen haberdar olun</p>
                    </div>
                  </div>
                  <div className="mobile-feature">
                    <FaShieldAlt className="mobile-feature-icon" />
                    <div>
                      <h4>Mobil Uygulama</h4>
                      <p>
                        Randevularınızı takip edin, başvuru sürecinizi izleyin
                        ve danışmanınızla anlık iletişim kurun
                      </p>
                    </div>
                  </div>
                </div>

                <div className="app-store-buttons">
                  <button
                    className="app-store-btn apple-store"
                    onClick={(e) => e.preventDefault()}
                  >
                    <div className="app-store-icon">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                      </svg>
                    </div>
                    <div className="app-store-text">
                      <span className="download-text">Download on the</span>
                      <span className="store-name">App Store</span>
                    </div>
                  </button>

                  <button
                    className="app-store-btn google-play"
                    onClick={(e) => e.preventDefault()}
                  >
                    <div className="app-store-icon">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                      </svg>
                    </div>
                    <div className="app-store-text">
                      <span className="download-text">GET IT ON</span>
                      <span className="store-name">Google Play</span>
                    </div>
                  </button>
                </div>

                <div className="mobile-trust-indicators">
                  <div className="mobile-trust-item">
                    <FaDownload className="mobile-trust-icon" />
                    <span>1.000+ İndirme</span>
                  </div>
                  <div className="mobile-trust-item">
                    <FaStar className="mobile-trust-icon" />
                    <span>4.8 Yıldız Değerlendirme</span>
                  </div>
                  <div className="mobile-trust-item">
                    <FaShieldAlt className="mobile-trust-icon" />
                    <span>SSL Güvenlik Sertifikası</span>
                  </div>
                </div>
              </div>

              <div className="mobile-app-mockup">
                <div className="phone-frame">
                  <div className="dynamic-island"></div>
                  <div className="phone-screen">
                    <div className="phone-content">
                      <div className="app-header">
                        <div className="status-bar">
                          <span className="time">9:41</span>
                          <div className="status-icons">
                            <div className="cellular-signal">
                              <div className="bar"></div>
                              <div className="bar"></div>
                              <div className="bar"></div>
                              <div className="bar"></div>
                            </div>
                            <div className="wifi-icon">
                              <svg
                                width="15"
                                height="11"
                                viewBox="0 0 15 11"
                                fill="white"
                              >
                                <path
                                  d="M1 4c2.5-2.5 6.5-2.5 9 0m-7 2c1.5-1.5 3.5-1.5 5 0m-3 2c0.5-0.5 1.5-0.5 2 0"
                                  stroke="white"
                                  strokeWidth="1"
                                  fill="none"
                                />
                              </svg>
                            </div>
                            <div className="battery-icon">
                              <div className="battery-body">
                                <div className="battery-level"></div>
                              </div>
                              <div className="battery-tip"></div>
                            </div>
                          </div>
                        </div>
                        <div className="app-title">
                          <div className="app-logo">
                            <FaGraduationCap />
                          </div>
                          <h3>Almanya Kampüs</h3>
                          <p className="welcome-text">Hoş geldiniz!</p>
                        </div>
                      </div>
                      <div className="app-dashboard">
                        <div className="dashboard-card featured">
                          <FaCalendarAlt />
                          <span>Randevularım</span>
                          <div className="card-badge">3</div>
                        </div>
                        <div className="dashboard-card">
                          <FaUniversity />
                          <span>Başvuru Takibi</span>
                        </div>
                        <div className="dashboard-card">
                          <FaBell />
                          <span>Bildirimler</span>
                          <div className="card-badge notification">2</div>
                        </div>
                        <div className="dashboard-card">
                          <FaUsers />
                          <span>Danışman İletişim</span>
                        </div>
                      </div>
                      <div className="app-footer">
                        <div className="app-nav-item active">
                          <FaGraduationCap />
                        </div>
                        <div className="app-nav-item">
                          <FaCalendarAlt />
                        </div>
                        <div className="app-nav-item">
                          <FaBell />
                        </div>
                        <div className="app-nav-item">
                          <FaUsers />
                        </div>
                      </div>
                    </div>
                    <div className="home-indicator"></div>
                  </div>
                  <div className="phone-shadow"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="contact-section" id="contact-form">
          <div className="container">
            <div className="contact-content">
              <div className="contact-info">
                <h2>Ücretsiz Görüşme Randevusu Alın</h2>
                <p>
                  0 Almanca ile Almanya'da ücretsiz eğitim hayalinizi
                  gerçekleştirmek için ilk adımı atın. Almanca bilmeden başvuru
                  sürecini beraber planlayalım ve hayallerinizi gerçeğe
                  dönüştürelim.
                </p>

                <div className="contact-details">
                  <a href="tel:+4915231390961" className="contact-item">
                    <span className="contact-icon-wrap">
                      <FaPhone className="contact-icon" />
                    </span>
                    <div>
                      <h4>Telefon</h4>
                      <p>+49 152 313 90 961</p>
                    </div>
                  </a>
                  <a
                    href="mailto:bilgi@almanyakampus.com"
                    className="contact-item"
                  >
                    <span className="contact-icon-wrap">
                      <FaEnvelope className="contact-icon" />
                    </span>
                    <div>
                      <h4>E-posta</h4>
                      <p>bilgi@almanyakampus.com</p>
                    </div>
                  </a>
                </div>
              </div>

              <div className="contact-form">
                {isSubmitted ? (
                  <div className="success-message">
                    <FaCheckCircle className="success-icon" />
                    <h3>Başvurunuz Alındı!</h3>
                    <p>En kısa sürede sizinle iletişime geçeceğiz.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label htmlFor="firstName">Adınız *</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        {...createFieldProps("firstName")}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="lastName">Soyadınız *</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        {...createFieldProps("lastName")}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">E-posta Adresi *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        {...createFieldProps("email")}
                      />
                    </div>

                    <div className="form-group">
                      <label>Telefon Numarası *</label>
                      <PhoneInput
                        country={"tr"}
                        preferredCountries={["tr", "de", "at"]}
                        value={formData.phone}
                        onChange={(phone) =>
                          setFormData((prev) => ({ ...prev, phone }))
                        }
                        onFocus={() => analytics.trackFormFieldFocus("phone")}
                        onBlur={(e) =>
                          analytics.trackFormFieldBlur("phone", e.target.value)
                        }
                        countryCodeEditable={false}
                        enableSearch={true}
                        disableSearchIcon={true}
                        searchPlaceholder="Ülke ara..."
                        searchNotFound="Ülke bulunamadı"
                        placeholder="+90 5xx xxx xx xx"
                        inputProps={{
                          name: "phone",
                          required: true,
                          autoComplete: "off",
                        }}
                        containerClass="phone-input-container"
                        inputClass="phone-input"
                        buttonClass="phone-dropdown"
                        dropdownClass="phone-country-dropdown"
                        searchClass="phone-country-search"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="age">Yaşınız *</label>
                      <input
                        type="number"
                        id="age"
                        name="age"
                        value={formData.age}
                        onChange={handleInputChange}
                        min="16"
                        max="65"
                        required
                        {...createFieldProps("age")}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="education">Eğitim Durumunuz *</label>
                      <select
                        id="education"
                        name="education"
                        value={formData.education}
                        onChange={handleInputChange}
                        required
                        {...createFieldProps("education")}
                      >
                        <option value="">Seçiniz</option>
                        <option value="lisans-ogrenci">Lisans Öğrencisi</option>
                        <option value="lisans-mezunu">Lisans Mezunu</option>
                        <option value="12-sinif">12. Sınıf</option>
                        <option value="diger">Diğer</option>
                      </select>
                    </div>

                    <button
                      type="submit"
                      className="submit-button"
                      disabled={isLoading}
                      onClick={() =>
                        analytics.trackButtonClick("form-submit", {
                          formComplete: Object.values(formData).every(
                            (val) => val
                          ),
                        })
                      }
                    >
                      <FaGraduationCap />
                      {isLoading
                        ? loadingMessage || "Bilgiler Kaydediliyor..."
                        : "Randevu Talebini Gönder"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Blog Section */}
        <BlogSection
          onOpenPost={navigateToBlogPost}
          onOpenList={navigateToBlogList}
        />

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-content">
              <div className="footer-text">
                <p>
                  &copy; 2025 Almanya Kampüs ÜniversiteDanışmanlığı. Tüm hakları
                  saklıdır.
                </p>
                <p>
                  Güvenilir danışmanlık hizmetiyle Almanya'da eğitim hayalinizi
                  gerçekleştirin.
                </p>
              </div>
              <button className="footer-cta" onClick={scrollToForm}>
                <FaArrowRight /> Son Şansınızı Kaçırmayın
              </button>
            </div>
          </div>
        </footer>

        {/* Sticky mobile CTA */}
        {!isSubmitted && (
          <div className="mobile-cta-bar">
            <button
              className="mobile-cta-button"
              onClick={() => {
                analytics.trackButtonClick("mobile-sticky-cta", {
                  section: "sticky",
                });
                scrollToForm();
              }}
            >
              <FaGraduationCap /> Ücretsiz Randevu Al
            </button>
          </div>
        )}
        {/* Vercel Speed Insights injects a same-origin script that resolves to
            HTML during react-snap prerendering (→ "Unexpected token '<'" page
            error that aborts the crawl). It's a client-only tool anyway, so
            skip it while prerendering. */}
        {navigator.userAgent !== "ReactSnap" && <SpeedInsights />}
      </div>
    </SecurityProvider>
  );
}

export default App;
